import { ApiError } from '../api/client'
import {
  AUTH_MESSAGES,
  GIS_INIT_OPTIONS,
  GOOGLE_AUTH_ERROR,
  GOOGLE_BUTTON_OPTIONS,
  GOOGLE_GSI_SCRIPT_SRC,
} from '../constants/auth'

type GoogleCredentialResponse = {
  credential?: string
}

type GoogleButtonRenderOptions = {
  type?: string
  theme?: string
  size?: string
  text?: string
  shape?: string
  width?: number
  logo_alignment?: string
}

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
    ux_mode?: 'popup' | 'redirect'
  }) => void
  prompt: (notification?: (notification: {
    isNotDisplayed: () => boolean
    isSkippedMoment: () => boolean
  }) => void) => void
  renderButton: (parent: HTMLElement, options: GoogleButtonRenderOptions) => void
}

export function resolveGoogleClientId(fromSettings?: string | null): string {
  const fromApi = fromSettings?.trim() ?? ''
  if (fromApi) return fromApi

  const fromVite = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ?? ''
  if (fromVite) return fromVite

  return window.__RUNTIME_CONFIG__?.GOOGLE_CLIENT_ID?.trim() ?? ''
}

let scriptPromise: Promise<void> | null = null

function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  const src = GOOGLE_GSI_SCRIPT_SRC

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Failed to load Google script')))
      if (window.google?.accounts?.id) resolve()
      return
    }

    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google script'))
    document.head.appendChild(script)
  })

  return scriptPromise
}

function googleAccountsId(): GoogleAccountsId | undefined {
  return window.google?.accounts?.id as GoogleAccountsId | undefined
}

function requireClientId(clientId: string): string {
  const resolved = clientId.trim()
  if (!resolved) {
    throw new Error(GOOGLE_AUTH_ERROR.missingClient)
  }
  return resolved
}

/** Returns a Google ID token via GIS One Tap / FedCM prompt. */
export async function requestGoogleIdToken(clientId: string): Promise<string> {
  const resolved = requireClientId(clientId)
  await loadGoogleScript()
  const googleId = googleAccountsId()
  if (!googleId) {
    throw new Error(GOOGLE_AUTH_ERROR.sdkUnavailable)
  }

  return new Promise((resolve, reject) => {
    let settled = false

    googleId.initialize({
      client_id: resolved,
      callback: (response) => {
        if (settled) return
        settled = true
        if (response.credential) {
          resolve(response.credential)
        } else {
          reject(new Error(GOOGLE_AUTH_ERROR.noCredential))
        }
      },
      auto_select: GIS_INIT_OPTIONS.autoSelect,
      cancel_on_tap_outside: GIS_INIT_OPTIONS.cancelOnTapOutside,
      ux_mode: GIS_INIT_OPTIONS.uxMode,
    })

    googleId.prompt((notification) => {
      if (settled) return
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        settled = true
        reject(new Error(GOOGLE_AUTH_ERROR.promptDismissed))
      }
    })
  })
}

export async function mountGoogleSignInButton(
  host: HTMLElement,
  onCredential: (idToken: string) => void | Promise<void>,
  clientId: string,
): Promise<() => void> {
  const resolved = requireClientId(clientId)
  await loadGoogleScript()
  const googleId = googleAccountsId()
  if (!googleId?.renderButton) {
    throw new Error(GOOGLE_AUTH_ERROR.sdkUnavailable)
  }

  host.replaceChildren()
  googleId.initialize({
    client_id: resolved,
    callback: (response) => {
      if (response.credential) void onCredential(response.credential)
    },
    auto_select: GIS_INIT_OPTIONS.autoSelect,
    cancel_on_tap_outside: GIS_INIT_OPTIONS.cancelOnTapOutside,
    ux_mode: GIS_INIT_OPTIONS.uxMode,
  })
  googleId.renderButton(host, {
    type: GOOGLE_BUTTON_OPTIONS.type,
    theme: GOOGLE_BUTTON_OPTIONS.theme,
    size: GOOGLE_BUTTON_OPTIONS.size,
    text: GOOGLE_BUTTON_OPTIONS.text,
    shape: GOOGLE_BUTTON_OPTIONS.shape,
    width: Math.max(host.clientWidth || 0, GOOGLE_BUTTON_OPTIONS.minWidth),
    logo_alignment: GOOGLE_BUTTON_OPTIONS.logoAlignment,
  })

  return () => {
    host.replaceChildren()
  }
}

export function googleAuthErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error && error.message === GOOGLE_AUTH_ERROR.missingClient) {
    return AUTH_MESSAGES.googleMissingClient
  }
  return AUTH_MESSAGES.googleFailed
}
